import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { clientsAPI } from '../../services/api';
import { 
  Lock, 
  Mail, 
  Eye, 
  EyeOff, 
  ShieldCheck, 
  Truck, 
  BarChart3, 
  ChevronRight, 
  Building,
  KeyRound, 
  Sparkles,
  Shield,
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  Smartphone,
  Check
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import BrandLogo from '../../components/BrandLogo';
import loginBg from '../../assets/loginbg.png';

export default function LoginPage() {
  const [selectedPortal, setSelectedPortal] = useState('client'); // 'client' | 'vendor'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSessionError, setActiveSessionError] = useState(null);
  const [tenantConfigError, setTenantConfigError] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);

  // 2FA State
  const [twoFactorData, setTwoFactorData] = useState(null); // { tempToken, email, name, isSuperAdmin }
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);
  
  const { login, verify2FA, switchPortal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const tenantParam = queryParams.get('tenant') || queryParams.get('t') || queryParams.get('client_code');
  const portalParam = queryParams.get('portal');

  useEffect(() => {
    if (portalParam === 'vendor' || portalParam === 'client') {
      setSelectedPortal(portalParam);
    }
  }, [portalParam]);

  useEffect(() => {
    if (tenantParam) {
      clientsAPI.getPublicBranding(tenantParam)
        .then(res => {
          if (res.data?.success && res.data?.tenant) {
            setTenantInfo(res.data.tenant);
          } else {
            setTenantInfo(null);
          }
        })
        .catch(() => setTenantInfo(null));
    }
  }, [tenantParam]);

  const handleSelectPortal = (portal) => {
    setSelectedPortal(portal);
    setActiveSessionError(null);
    setTenantConfigError(null);
  };

  const handleForceLogin = async () => {
    setIsLoading(true);
    setActiveSessionError(null);
    setTenantConfigError(null);
    try {
      const res = await login(email, password, rememberMe, true);
      if (res?.data?.requires2FA) {
        setTwoFactorData(res.data);
        toast.success('Security Verification: Enter your 6-digit code.');
        return;
      }

      const u = res?.data?.user || res?.data;
      const roles = Array.isArray(u?.role) ? u.role : (Array.isArray(u?.roles) ? u.roles.map(r => r?.name || r) : [u?.role].filter(Boolean));
      const isSuper = u?.type === 'SuperAdmin' || roles.includes('SuperAdmin') || u?.email === 'connect@sanglob.com' || u?.email === 'superadmin@sanglob.local';

      if (isSuper) {
        toast.success('SuperAdmin authenticated. Entering Platform Control Tower.');
        navigate('/superadmin/control-tower', { replace: true });
        return;
      }
      
      switchPortal(selectedPortal);
      localStorage.setItem('activePortal', selectedPortal);
      toast.success('Session override accepted. Welcome back!');

      if (selectedPortal === 'vendor' || u?.portal_access === 'Vendor Portal' || u?.type === 'Vendor') {
        navigate('/vendor/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Force login error:', error);
      toast.error('Failed to force login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActiveSessionError(null);
    setTenantConfigError(null);
    
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const res = await login(email, password, rememberMe, false, twoFactorCode.trim());
      if (res?.data?.requires2FA) {
        setTwoFactorData(res.data);
        toast.success('Security Verification Required');
        return;
      }

      const u = res?.data?.user || res?.data;
      const roles = Array.isArray(u?.role) ? u.role : (Array.isArray(u?.roles) ? u.roles.map(r => r?.name || r) : [u?.role].filter(Boolean));
      const isSuper = u?.type === 'SuperAdmin' || roles.includes('SuperAdmin') || u?.email === 'connect@sanglob.com' || u?.email === 'superadmin@sanglob.local';

      if (isSuper) {
        toast.success('Welcome SuperAdmin! Entering Platform Control Tower.');
        navigate('/superadmin/control-tower', { replace: true });
        return;
      }

      switchPortal(selectedPortal);
      localStorage.setItem('activePortal', selectedPortal);
      toast.success(`Welcome back! Entering ${selectedPortal === 'vendor' ? 'Vendor Portal' : 'Client Workspace'}.`);
      
      if (selectedPortal === 'vendor' || u?.portal_access === 'Vendor Portal' || u?.type === 'Vendor') {
        navigate('/vendor/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      if (error.response?.data?.code === 'ACTIVE_SESSION_EXISTS') {
        setActiveSessionError({
          message: error.response.data.message || 'Another session is active with this account.',
          detail: 'Click below to terminate previous sessions and enter your portal.'
        });
        return;
      }

      if (
        error.response?.data?.code === 'TENANT_CONFIG_REQUIRED' || 
        error.response?.data?.code === 'LICENSE_REQUIRED' || 
        error.response?.data?.code === 'SUBSCRIPTION_REQUIRED' ||
        error.response?.data?.code === 'TENANT_INACTIVE'
      ) {
        setTenantConfigError(error.response.data.message || 'Tenant configuration incomplete. Contact Platform SuperAdmin to configure your client license and subscription.');
        return;
      }

      if (error.response?.data?.code === 'LICENSE_EXPIRED' && error.response.data.renew_url) {
        toast.error('Subscription has expired. Redirecting to marketplace...');
        setTimeout(() => {
          window.location.href = error.response.data.renew_url;
        }, 2000);
        return;
      }

      const errorMessage = error.response?.data?.message || 'Authentication failed. Please verify credentials.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFactorCode || twoFactorCode.trim().length === 0) {
      toast.error('Please enter the 6-digit verification code or backup code');
      return;
    }

    setIsVerifying2FA(true);
    try {
      const res = await verify2FA(twoFactorData.tempToken, twoFactorCode.trim(), rememberMe);
      const u = res?.data?.user || res?.data;
      const roles = Array.isArray(u?.role) ? u.role : (Array.isArray(u?.roles) ? u.roles.map(r => r?.name || r) : [u?.role].filter(Boolean));
      const isSuper = u?.type === 'SuperAdmin' || roles.includes('SuperAdmin') || u?.email === 'connect@sanglob.com' || u?.email === 'superadmin@sanglob.local';

      if (isSuper) {
        toast.success('Two-Factor Authentication verified. Entering Control Tower.');
        navigate('/superadmin/control-tower', { replace: true });
        return;
      }

      switchPortal(selectedPortal);
      localStorage.setItem('activePortal', selectedPortal);
      toast.success(`Identity Verified. Welcome back!`);

      if (selectedPortal === 'vendor' || u?.portal_access === 'Vendor Portal' || u?.type === 'Vendor') {
        navigate('/vendor/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsVerifying2FA(false);
    }
  };

  const isVendor = selectedPortal === 'vendor';

  return (
    <div className="h-screen w-full flex flex-col justify-between font-sans bg-gradient-to-br from-slate-50 via-blue-50/25 to-slate-100 text-slate-900 selection:bg-blue-600 selection:text-white overflow-hidden relative">
      
      {/* Ambient Background Soft Light Gradients */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(59,130,246,0.06),transparent_35%),radial-gradient(circle_at_88%_82%,rgba(99,102,241,0.05),transparent_35%)] pointer-events-none" />

      {/* 1. TOP ROYAL BLUE HEADER BAR */}
      <header className="w-full bg-[#1e40af] bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 border-b border-blue-700/60 px-6 sm:px-10 py-2.5 flex items-center justify-between z-20 shadow-md shrink-0">
        <div className="flex items-center gap-3">
          <BrandLogo className="h-7 sm:h-8" dark={true} />
          <div className="hidden sm:flex items-center gap-2.5 pl-3 border-l border-blue-400/30">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-950/40 text-blue-100 text-xs font-semibold border border-blue-400/30 shadow-2xs backdrop-blur-xs">
              <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
              <span>Enterprise Platform v2.6</span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/superadmin/login"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-blue-700/60 hover:bg-blue-600 border border-blue-400/40 hover:border-blue-300 transition-all shadow-xs"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-cyan-300" />
            <span>SuperAdmin Portal</span>
          </Link>
        </div>
      </header>

      {/* 2. MIDDLE SECTION: Framed Canvas with Centered Background & Right Login Card */}
      <main className="flex-1 w-full max-w-[1560px] 2xl:max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3.5 pb-12 flex items-center justify-center min-h-0 overflow-hidden relative">
        
        {/* Centered Framed Canvas with Rounded Border & Background Image */}
        <div className="w-full h-full max-h-[calc(100vh-105px)] rounded-2xl overflow-hidden border border-slate-200/90 shadow-2xl shadow-blue-900/10 relative flex items-center justify-end p-3 sm:p-5 lg:p-6 bg-slate-900/5">
          
          {/* Background Graphic: Beautifully placed in center without stretching */}
          <div className="absolute inset-0 z-0">
            <img 
              src={loginBg} 
              alt="Global AI ERP Platform Background" 
              className="w-full h-full object-cover object-center select-none pointer-events-none"
            />
          </div>

          {/* Foreground Content: Right Floating Single Column Login Card (Increased by 20% width) */}
          <div className="relative z-10 w-full max-w-lg lg:max-w-none lg:w-[38.5%] xl:w-[33.5%] 2xl:w-[30%] h-full max-h-full bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/90 shadow-2xl shadow-blue-950/20 p-4 sm:p-5.5 flex flex-col justify-between overflow-y-auto stylish-scrollbar shrink-0 mx-auto lg:mx-0">
            
            <div className="w-full space-y-3 my-auto">

              {/* Top Badges inside the Login Section */}
              <div className="flex items-center justify-between gap-2 pb-2.5 border-b border-slate-100">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-bold border border-blue-200/80 shadow-2xs">
                  <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
                  <span>Next-Gen Connected ERP</span>
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 text-[11px] font-semibold border border-emerald-200/80 shadow-2xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span>Continuous Mesh</span>
                </span>
              </div>

              {/* Custom Tenant Header if Accessed via Tenant Vanity URL */}
              {tenantInfo && (
                <div className="p-2 bg-blue-50/80 border border-blue-200 rounded-xl flex items-center gap-2 shadow-2xs">
                  {tenantInfo.logo_url ? (
                    <img src={tenantInfo.logo_url} alt={tenantInfo.name} className="h-5 w-auto object-contain" />
                  ) : (
                    <div className="w-5 h-5 rounded-md bg-blue-600 text-white font-bold flex items-center justify-center text-[11px] shadow-xs">
                      {tenantInfo.name?.charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs font-bold text-slate-900 truncate">{tenantInfo.name}</span>
                    <span className="block text-[9.5px] text-slate-500 truncate">{tenantInfo.client_code} • Dedicated Tenant</span>
                  </div>
                </div>
              )}

              {twoFactorData ? (
                /* 2FA VERIFICATION STEP */
                <div className="space-y-3 animate-in fade-in slide-in-from-right-3 duration-200">
                  <div className="space-y-1">
                    <div className="w-8 h-8 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 mb-1">
                      <Smartphone className="w-4 h-4" />
                    </div>
                    <h2 className="text-base font-extrabold text-slate-900 tracking-tight">
                      Security Verification
                    </h2>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Enter the 6-digit verification code or backup code.
                    </p>
                    <p className="text-[11px] font-semibold text-slate-700 font-mono truncate">
                      {twoFactorData.email}
                    </p>
                  </div>

                  <form onSubmit={handleVerify2FA} className="space-y-2.5">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-bold text-slate-700">
                          Security Code
                        </label>
                        <button
                          type="button"
                          onClick={() => setTwoFactorCode('123456')}
                          className="text-[10.5px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200"
                        >
                          <span>⚡ Test (123456)</span>
                        </button>
                      </div>
                      <div className="relative">
                        <KeyRound className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
                        <input
                          type="text"
                          required
                          autoFocus
                          maxLength={12}
                          value={twoFactorCode}
                          onChange={(e) => setTwoFactorCode(e.target.value)}
                          className="w-full pl-9 pr-3 py-1.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-xl text-sm font-mono font-bold tracking-widest text-slate-900 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-600 transition-all text-center"
                          placeholder="123456"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isVerifying2FA}
                      className="w-full py-2 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-xs rounded-xl shadow-md shadow-blue-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                    >
                      {isVerifying2FA ? (
                        <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      <span>{isVerifying2FA ? 'Verifying...' : 'Verify & Enter'}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => { setTwoFactorData(null); setTwoFactorCode(''); }}
                      className="w-full py-1 text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      <span>Back to login</span>
                    </button>
                  </form>
                </div>
              ) : (
                /* STANDARD LOGIN STEP (Single Column Layout) */
                <div className="space-y-2.5">
                  
                  {/* Title & Portal Segment Tabs */}
                  <div className="space-y-1.5">
                    <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">
                      Sign In
                    </h2>
                    
                    {/* Clean Segmented Workspace Selector */}
                    <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100/90 rounded-xl border border-slate-200/80">
                      <button
                        type="button"
                        onClick={() => handleSelectPortal('client')}
                        className={clsx(
                          "flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-xs font-bold transition-all cursor-pointer",
                          !isVendor
                            ? "bg-white text-blue-700 shadow-xs border border-slate-200/70"
                            : "text-slate-600 hover:text-slate-900"
                        )}
                      >
                        <Building className="w-3.5 h-3.5 text-blue-600" />
                        <span>Client</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleSelectPortal('vendor')}
                        className={clsx(
                          "flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-xs font-bold transition-all cursor-pointer",
                          isVendor
                            ? "bg-white text-emerald-700 shadow-xs border border-slate-200/70"
                            : "text-slate-600 hover:text-slate-900"
                        )}
                      >
                        <Truck className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Vendor</span>
                      </button>
                    </div>
                  </div>

                  {/* Tenant Configuration Warning */}
                  {tenantConfigError && (
                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-xl text-xs space-y-1">
                      <div className="flex items-center gap-1.5 text-amber-900 font-bold">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        <span>Tenant Restricted</span>
                      </div>
                      <p className="text-[10.5px] text-amber-800 leading-tight">
                        {tenantConfigError}
                      </p>
                    </div>
                  )}

                  {/* Active Session Warning */}
                  {activeSessionError && (
                    <div className="p-2 bg-rose-50 border border-rose-200 rounded-xl text-xs space-y-1">
                      <div className="flex items-center gap-1.5 text-rose-900 font-bold">
                        <ShieldCheck className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                        <span>Active Session Detected</span>
                      </div>
                      <p className="text-[10.5px] text-rose-700 leading-tight">{activeSessionError.detail}</p>
                      <button
                        type="button"
                        onClick={handleForceLogin}
                        className="w-full py-1 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer mt-1"
                      >
                        Terminate Previous Session & Enter
                      </button>
                    </div>
                  )}

                  {/* Login Form: Single Column Fields */}
                  <form onSubmit={handleSubmit} className="space-y-2">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-0.5">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full pl-9 pr-3 py-1.5 bg-slate-50/80 hover:bg-white focus:bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-600 transition-all"
                          placeholder={isVendor ? "supplier@company.com" : "admin@client.com"}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-0.5">
                        <label className="block text-xs font-bold text-slate-700">
                          Password
                        </label>
                        <Link to="/forgot-password" className="text-[10.5px] font-semibold text-blue-600 hover:text-blue-800">
                          Forgot?
                        </Link>
                      </div>
                      <div className="relative">
                        <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          required
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full pl-9 pr-9 py-1.5 bg-slate-50/80 hover:bg-white focus:bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-600 transition-all"
                          placeholder="••••••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-700 p-0.5 cursor-pointer"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Direct 2FA Security Code Field */}
                    <div>
                      <div className="flex items-center justify-between mb-0.5">
                        <label className="block text-xs font-bold text-slate-700">
                          2FA Code <span className="text-slate-400 font-normal">(Optional)</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setTwoFactorCode('123456')}
                          className="text-[10.5px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200/80"
                        >
                          <span>⚡ Test (123456)</span>
                        </button>
                      </div>
                      <div className="relative">
                        <KeyRound className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
                        <input
                          type="text"
                          maxLength={12}
                          value={twoFactorCode}
                          onChange={(e) => setTwoFactorCode(e.target.value)}
                          className="w-full pl-9 pr-3 py-1.5 bg-slate-50/80 hover:bg-white focus:bg-white border border-slate-200 rounded-xl text-xs font-mono font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-600 transition-all"
                          placeholder="e.g. 123456"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-0.5">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                        />
                        <span className="text-xs text-slate-600 font-medium">Keep me signed in</span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className={clsx(
                        "w-full py-2 px-4 rounded-xl font-bold text-xs text-white shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer mt-1",
                        isVendor 
                          ? "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-emerald-600/20" 
                          : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-blue-600/20",
                        isLoading && "opacity-60 cursor-not-allowed"
                      )}
                    >
                      {isLoading ? (
                        <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                      <span>{isLoading ? 'Authenticating...' : `Enter ${isVendor ? 'Vendor Portal' : 'Client Workspace'}`}</span>
                    </button>
                  </form>

                </div>
              )}

            </div>

            {/* Bottom 3 Feature Pillars in Right Login Section */}
            <div className="pt-2 mt-2 border-t border-slate-100 space-y-1.5 shrink-0">
              
              {/* Pillar 1: Financials */}
              <div className="p-1.5 rounded-lg bg-slate-50/90 hover:bg-white border border-slate-200/70 transition-all flex items-center gap-2 shadow-2xs">
                <div className="w-5 h-5 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 shrink-0">
                  <BarChart3 className="w-3 h-3" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-[10.5px] font-bold text-slate-900 leading-none">Financials & Tax</h4>
                  <p className="text-[9px] text-slate-500 truncate mt-0.5 font-medium">GL journals, GST/VAT schedules & fiscal controls</p>
                </div>
              </div>

              {/* Pillar 2: Supplier Mesh */}
              <div className="p-1.5 rounded-lg bg-slate-50/90 hover:bg-white border border-slate-200/70 transition-all flex items-center gap-2 shadow-2xs">
                <div className="w-5 h-5 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 shrink-0">
                  <Truck className="w-3 h-3" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-[10.5px] font-bold text-slate-900 leading-none">Supplier Mesh</h4>
                  <p className="text-[9px] text-slate-500 truncate mt-0.5 font-medium">RFx bidding, verified POs & 3-way matching</p>
                </div>
              </div>

              {/* Pillar 3: Zero-Trust Security */}
              <div className="p-1.5 rounded-lg bg-slate-50/90 hover:bg-white border border-slate-200/70 transition-all flex items-center gap-2 shadow-2xs">
                <div className="w-5 h-5 rounded-md bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-600 shrink-0">
                  <Shield className="w-3 h-3" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-[10.5px] font-bold text-slate-900 leading-none">Zero-Trust Security</h4>
                  <p className="text-[9px] text-slate-500 truncate mt-0.5 font-medium">RSA-4096 licensing, 2FA & tenant isolation</p>
                </div>
              </div>

            </div>

          </div>

        </div>
      </main>

      {/* 3. FIXED BOTTOM GLOBAL FOOTER BAR */}
      <footer className="fixed bottom-0 left-0 right-0 w-full bg-white/95 backdrop-blur-md border-t border-slate-200/90 px-6 sm:px-10 py-2 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500 z-30 shadow-xs">
        <div className="flex items-center gap-2 text-slate-600 flex-wrap">
          <span className="font-semibold text-slate-800">SOC 2 & ISO Certified</span>
          <span>•</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse" />
            <span className="text-emerald-700 font-medium">99.99% Availability</span>
          </span>
          <span>•</span>
          <span>Secure Session Guard</span>
          <span>•</span>
          <span className="font-medium text-slate-700">Sanglob Cloud</span>
        </div>
        <div className="text-slate-500 text-[11px]">
          <span>Protected by Cryptographic RSA-4096 & Zero-Trust RBAC · © {new Date().getFullYear()} Sanglob Platform</span>
        </div>
      </footer>

    </div>
  );
}
